import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
// F3-1 / denetim #29: raw axios → apiClient (401-refresh-retry interceptor)
import axios from '../utils/api';
import { useAuth } from './AuthContext';

// NOT: Kredi sistemi kaldırıldı. Bu context artık yalnızca günlük AI kotasını
// (Kayıt Duvarı: günde 3 sorgu) yönetir. Geriye dönük uyumluluk için
// `CreditProvider`/`useCredits` isimleri korunmuştur; kredi bakiyesi yoktur.
const CreditContext = createContext();

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (!context) {
    console.warn('useCredits is being used outside of CreditProvider. Returning safe fallback values.');
    return {
      dailyQuota: null,
      loading: false,
      refreshQuota: () => Promise.resolve({ success: false, error: 'Not in CreditProvider' }),
      refreshCredits: () => Promise.resolve({ success: false, error: 'Not in CreditProvider' }),
      loadCredits: () => Promise.resolve({ success: false, error: 'Not in CreditProvider' })
    };
  }
  return context;
};

export const CreditProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  // Kayıt Duvarı modeli: AI sorguları kredi düşmez — gösterilecek sayı
  // günlük kotadır (3/gün). { limit, used, remaining, unlimited, resetsAt }
  const [dailyQuota, setDailyQuota] = useState(null);
  const lastQuotaFetchRef = useRef(0);

  // Cache duration: 30 seconds
  const CACHE_DURATION = 30 * 1000;

  const fetchDailyQuota = useCallback((forceRefresh = false) => {
    if (!user) return Promise.resolve({ success: false, error: 'User not authenticated' });
    const now = Date.now();
    if (!forceRefresh && now - lastQuotaFetchRef.current < CACHE_DURATION) {
      return Promise.resolve({ success: true, cached: true });
    }
    setLoading(true);
    return axios.get('/quota/daily-quota')
      .then((response) => {
        if (response.data?.success) setDailyQuota(response.data);
        lastQuotaFetchRef.current = Date.now();
        return response.data;
      })
      .catch(() => { /* kota alınamazsa gösterge sadece plan adını gösterir */ })
      .finally(() => setLoading(false));
  }, [user, CACHE_DURATION]);

  // Oturum açınca günlük kotayı çek; oturum kapanınca sıfırla
  useEffect(() => {
    if (!user) {
      setDailyQuota(null);
      lastQuotaFetchRef.current = 0;
      return;
    }
    fetchDailyQuota();
  }, [user, fetchDailyQuota]);

  // Geriye dönük uyumluluk: eski kredi tüketicileri artık kotayı tazeler.
  const refreshQuota = useCallback((forceRefresh = false) => fetchDailyQuota(forceRefresh), [fetchDailyQuota]);

  const value = {
    dailyQuota,
    loading,
    refreshQuota,
    refreshCredits: refreshQuota,
    loadCredits: refreshQuota
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};